import { useState } from "react";
import { registerUser } from "../services/authService";
import { _post, _get } from "../services/apiClient";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
// import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { apiUrls } from "../config/apiUrls.const";
import { showToast } from "../services/alertService";
import { setSecureItem } from "../utils/LocalStorageManager";

type ValidationErrors = {
  [key: string]: string[];
};

const markerIcon = new L.Icon({
  iconUrl: "./marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

type LatLng = {
  lat: number;
  lng: number;
};

function LocationPicker({ onPick }: { onPick: (latlng: LatLng) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng);
    },
  });
  return null;
}

export default function Register() {
  const [formData, setFormData] = useState({
    name: "",
    mobileNo: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreement: false,
    isBusinessClient: false,
    type: "member",
    allowed_service: "sparloon",
  });

  const [businessFormData, setBusinessFormData] = useState({
    businessName: "",
    mobileNo: "",
    email: "",
    address: "",
    location: null as LatLng | null,
  });

  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [businessNameExists, setBusinessNameExists] = useState(false);

  // Update main form
  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        e.target.type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : e.target.value;

      // Set type automatically
      if (field === "isBusinessClient") {
        setFormData((prev) => ({
          ...prev,
          [field]: value as boolean,
          type: value ? "client" : "member",
        }));
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
      }

      // Sync business form mobile/email
      if (field === "mobileNo") {
        setBusinessFormData((prev) => ({ ...prev, mobileNo: value.toString() }));
      }
      if (field === "email") {
        setBusinessFormData((prev) => ({ ...prev, email: value.toString() }));
      }

      if (errors[field]) {
        setErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
    };

  // Update business form
  const handleBusinessInputChange =
    (field: keyof typeof businessFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setBusinessFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
    };

  // Check business name availability
  const checkBusinessName = async (): Promise<boolean> => {
    const name = businessFormData.businessName.trim();
    if (!name) return false;

    try {
      const response = await _post(apiUrls.checkBusinessName, { name });
      const checkData = response.data;

      if (checkData.data) {
        setBusinessNameExists(true);
        return true; // exists
      } else {
        setBusinessNameExists(false);
        return false; // available
      }
    } catch (err) {
      console.error("Error checking business name:", err);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setErrors({});

    // If registering as business client → must check business name
    if (formData.isBusinessClient) {
        const exists = await checkBusinessName();
        if (exists) {
        showToast({
            icon: "warning",
            title: "Business name already exists",
            text: "Please choose another one.",
        });
        setLoading(false);
        return;
        }

        // Validate business form
        const newErrors: ValidationErrors = {};
        if (!businessFormData.businessName)
        newErrors.business_name = ["Business Name is required"];
        if (!businessFormData.address)
        newErrors.address = ["Address is required"];
        if (!businessFormData.location)
        newErrors.location = ["Please select a location on the map"];

        if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        // 🔹 Show each validation error in toast
        Object.entries(newErrors).forEach(([field, msgs]) =>
            msgs.forEach((msg) =>
            showToast({ icon: "error", title: "Validation Error", text: msg })
            )
        );
        setLoading(false);
        return;
        }
    }

    try {
        // Register user
        const userData = await registerUser(
        formData.name,
        formData.mobileNo,
        formData.email,
        formData.password,
        formData.confirmPassword,
        formData.type,
        formData.allowed_service
        );

        if (!userData.success) {
        if (userData.type === "validation") {
            setErrors(userData.errors);
            // 🔹 Show backend validation errors
            Object.entries(userData.errors).forEach(([field, msgs]) =>
                (msgs as string[]).forEach((msg: string) =>
                    showToast({ icon: "error", title: "Validation Error", text: msg })
                )
            );
        } else {
            setMessage(userData.message || "Registration failed.");
            showToast({
            icon: "error",
            title: "Error",
            text: userData.message || "Registration failed.",
            });
        }
        setLoading(false);
        return;
        }

        // If business client → store business info
        if (formData.isBusinessClient) {
        try {
            const businesses = await _post(apiUrls.storeBusiness, {
              name: businessFormData.businessName,
              address: businessFormData.address,
              mobile_no: businessFormData.mobileNo,
              email: businessFormData.email,
              lat: businessFormData.location?.lat,
              lon: businessFormData.location?.lng,
            });
            setSecureItem("businesses", JSON.stringify(businesses?.data?.data || []));
        } catch (err: any) {
            setMessage(err.message || "Business registration failed.");
            showToast({
            icon: "error",
            title: "Business Error",
            text: err.message || "Business registration failed.",
            });
            setLoading(false);
            return;
        }
        }

        setMessage("Registration successful! Reloading...");
        showToast({
        icon: "success",
        title: "Success",
        text: "Registration successful! Reloading...",
        });
        window.location.reload();
    } catch (err: any) {
        setMessage(err.message || "Something went wrong.");
        showToast({
        icon: "error",
        title: "Error",
        text: err.message || "Something went wrong.",
        });
    } finally {
        setLoading(false);
    }
    };

  return (
    <>
      {Object.keys(errors).length > 0 && (
        <ul className="text-danger">
          {Object.entries(errors).map(([field, msgs]) =>
            msgs.map((msg, idx) => <li key={`${field}-${idx}`}>{msg}</li>)
          )}
        </ul>
      )}

      <form onSubmit={handleSubmit}>
        {/* Name */}
        <div className="mb-3">
          <label>Name</label>
          <input
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
            value={formData.name}
            onChange={handleInputChange("name")}
            required
          />
        </div>

        {/* Mobile */}
        <div className="mb-3">
          <label>Mobile No</label>
          <input
            className={`form-control ${errors.mobile_no ? "is-invalid" : ""}`}
            value={formData.mobileNo}
            onChange={handleInputChange("mobileNo")}
            required
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <label>Email</label>
          <input
            type="email"
            className={`form-control ${errors.email ? "is-invalid" : ""}`}
            value={formData.email}
            onChange={handleInputChange("email")}
            required
          />
        </div>

        {/* Password */}
        <div className="mb-3">
          <label>Password</label>
          <input
            type="password"
            className={`form-control ${errors.password ? "is-invalid" : ""}`}
            value={formData.password}
            onChange={handleInputChange("password")}
            required
          />
        </div>

        {/* Confirm Password */}
        <div className="mb-3">
          <label>Confirm Password</label>
          <input
            type="password"
            className={`form-control ${errors.password_confirmation ? "is-invalid" : ""}`}
            value={formData.confirmPassword}
            onChange={handleInputChange("confirmPassword")}
            required
          />
        </div>

        {/* Agreement */}
        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input dark-border"
            id="agreement"
            checked={formData.agreement}
            onChange={handleInputChange("agreement")}
            required
          />
          <label className="form-check-label pointer" htmlFor="agreement">
            I agree to the terms and conditions
          </label>
        </div>

        {/* Business client toggle */}
        <div className="form-check form-switch mb-3">
          <input
            type="checkbox"
            className="form-check-input form-check-input-md dark-border mt-2"
            id="isBusinessClient"
            checked={formData.isBusinessClient}
            onChange={handleInputChange("isBusinessClient")}
          />
          <label className="form-check-label fs-120 fw-500 pointer" htmlFor="isBusinessClient">
            Register as Business Client
          </label>
        </div>

        {formData.isBusinessClient && (
          <>
            {/* Business Name */}
            <div className="mb-3">
              <label>Business Name</label>
              <input
                className={`form-control ${errors.business_name ? "is-invalid" : ""}`}
                value={businessFormData.businessName}
                onChange={handleBusinessInputChange("businessName")}
                onBlur={checkBusinessName}
                required
              />
              <div className={`text-danger ${businessNameExists ? "is-invalid d-block" : "d-none"}`}>Business name already exists</div>
            </div>

            {/* Address */}
            <div className="mb-3">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                className={`form-control ${errors.address ? "is-invalid" : ""}`}
                value={businessFormData.address}
                onChange={handleBusinessInputChange("address")}
                rows={2}
                required
              />
            </div>

            {/* Location Picker */}
            <div className="mb-3">
              <label>Pick Location</label>
              <div style={{ height: "360px", width: "100%" }}>
                <MapContainer
                  center={[23.8103, 90.4125]}
                  zoom={13}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                  <LocationPicker
                    onPick={(latlng) =>
                      setBusinessFormData((prev) => ({ ...prev, location: latlng }))
                    }
                  />
                  {businessFormData.location && (
                    <Marker position={businessFormData.location} icon={markerIcon} />
                  )}
                </MapContainer>
              </div>
              {errors.location && (
                <div className="text-danger mt-2">{errors.location[0]}</div>
              )}
            </div>

            <div className="mb-3">
              <b className="text-danger">Note:</b> Business registration includes a{" "}
              <span className="text-danger">45-day</span> trial. After that, a paid
              subscription is required to continue.
            </div>
          </>
        )}

        <button type="submit" className="btn btn-primary w-100" disabled={loading}>
          {loading ? "Registering..." : "Register"}
        </button>
      </form>
    </>
  );
}
